import * as React from 'react';
import { COMPANY_EMAIL_DOMAIN, companyEmail, isCompanyEmail, ROLES, type Role } from '@cfl/shared';

import {
  Alert, AlertDescription, Button, Combobox, Dialog, DialogContent, DialogFooter,
  DialogHeader, DialogTitle, Input, Label,
} from '@/components/cargofl-ui';
import { ApiError } from '@/lib/api';
import { useCreateEmployee, useUpdateEmployee } from '@/lib/admin-api';
import type { PersonRow } from '@/lib/people-api';

/** Minor units as a string, to what somebody would type: "150050" -> "1500.50". */
function minorToInput(minor: string | null): string {
  if (!minor || minor === '0') return '';
  const negative = minor.startsWith('-');
  const digits = (negative ? minor.slice(1) : minor).padStart(3, '0');
  const paise = digits.slice(-2);
  const major = digits.slice(0, -2);
  return `${negative ? '-' : ''}${major}${paise === '00' ? '' : `.${paise}`}`;
}

const ROLE_DESCRIPTION: Record<Role, string> = {
  EMPLOYEE: 'Records their own time and requests leave',
  SUPERVISOR: 'Also approves weeks and leave for their team',
  HR: 'Also administers people, contracts and teams',
  ADMIN: 'Also administers customers, projects and holidays',
};

/**
 * Create or edit an employee.
 *
 * Sign-in is not configured here: Google supplies the identity, and this record is
 * what decides whether that identity is allowed in and what it may do. Somebody
 * whose address has no record cannot sign in at all, which is why creating the
 * record is the whole of onboarding.
 */
export function EmployeeDialog({
  person,
  people,
  actorRole,
  actorId,
  onClose,
}: {
  /** null = creating. */
  person: PersonRow | null;
  people: PersonRow[];
  actorRole: Role;
  actorId: string;
  onClose: () => void;
}) {
  const create = useCreateEmployee();
  const update = useUpdateEmployee();
  const editing = person !== null;

  const [email, setEmail] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [alias, setAlias] = React.useState('');
  const [jobTitle, setJobTitle] = React.useState('');
  const [accountNumber, setAccountNumber] = React.useState('');
  const [timezone, setTimezone] = React.useState('Asia/Kolkata');
  const [role, setRole] = React.useState<Role>('EMPLOYEE');
  const [supervisorId, setSupervisorId] = React.useState<string | null>(null);
  const [hourlyRate, setHourlyRate] = React.useState('');
  const [internalRate, setInternalRate] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setEmail(person?.email ?? '');
    setDisplayName(person?.displayName ?? '');
    setAlias(person?.alias ?? '');
    setJobTitle(person?.jobTitle ?? '');
    setAccountNumber(person?.accountNumber ?? '');
    setTimezone(person?.timezone ?? 'Asia/Kolkata');
    setRole(person?.role ?? 'EMPLOYEE');
    setSupervisorId(person?.supervisor?.id ?? null);
    setHourlyRate(minorToInput(person?.hourlyRateMinor ?? null));
    setInternalRate(minorToInput(person?.internalRateMinor ?? null));
    setError(null);
  }, [person]);

  // Nobody may grant a role above their own; the API enforces it, and offering the
  // option anyway would just produce a refusal after the click.
  const assignableRoles = ROLES.filter(
    (r) => ROLES.indexOf(r) <= ROLES.indexOf(actorRole),
  );
  const editingSelf = editing && person.id === actorId;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      if (editing) {
        await update.mutateAsync({
          id: person.id,
          email,
          displayName,
          alias: alias.trim() || null,
          jobTitle: jobTitle.trim() || null,
          // These two were on the create payload only, so an edit silently threw
          // away any change to them - the form showed the new value and saved the
          // old one.
          accountNumber: accountNumber.trim() || null,
          timezone,
          // Sent even when empty: an empty string is how a rate is cleared, and
          // omitting it would leave the old one in place.
          hourlyRate: hourlyRate.trim(),
          internalRate: internalRate.trim(),
          // Omitted when editing yourself: the API refuses a self role change, and
          // sending the unchanged value would trip that check for no reason.
          ...(editingSelf ? {} : { role }),
          supervisorId,
        });
      } else {
        await create.mutateAsync({
          email,
          displayName,
          alias: alias.trim() || null,
          jobTitle: jobTitle.trim() || null,
          accountNumber: accountNumber.trim() || null,
          timezone,
          role,
          supervisorId,
          hourlyRate: hourlyRate.trim(),
          internalRate: internalRate.trim(),
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that person.');
    }
  };

  const busy = create.isPending || update.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit person' : 'Add a person'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Work email</Label>
            {/* Gmail-style: type the name, the domain is fixed beside it. A legacy record on
                another domain keeps a plain box — the fixed suffix would silently rewrite who
                they sign in as. The API refuses a new or changed off-domain address either way. */}
            {person && !isCompanyEmail(person.email) ? (
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            ) : (
              <div className="flex">
                <Input
                  id="email"
                  value={email.split('@')[0] ?? ''}
                  onChange={(e) => setEmail(companyEmail(e.target.value))}
                  placeholder="someone"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  className="rounded-r-none"
                  required
                />
                <span className="inline-flex shrink-0 select-none items-center rounded-r-md border border-l-0 bg-muted px-3 text-sm text-muted-foreground">
                  @{COMPANY_EMAIL_DOMAIN}
                </span>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              This is what they sign in with. It has to match their Google account.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="displayName">Full name</Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alias">Short name (optional)</Label>
              <Input id="alias" value={alias} onChange={(e) => setAlias(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="jobTitle">Job title (optional)</Label>
            <Input id="jobTitle" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
            <p className="text-xs text-muted-foreground">
              Shown in people lists. It grants nothing &mdash; access comes from the role below.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="accountNumber">Staff number (optional)</Label>
              <Input
                id="accountNumber"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Payroll's reference for this person. Carried into exports.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="timezone">Time zone</Label>
              <Input id="timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
              <p className="text-xs text-muted-foreground">
                {/* Decides the clock times on their entries, not which day work counts
                    towards - that follows the organisation's zone for everybody, so a
                    month closes on the same day for the whole team. */}
                An IANA name, like Asia/Kolkata. Sets the clock times shown on their entries.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Role</Label>
            {editingSelf ? (
              <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                {role.toLowerCase()} &mdash; you cannot change your own role. Ask another
                administrator.
              </p>
            ) : (
              <Combobox
                value={role}
                onChange={(v) => setRole(v as Role)}
                options={assignableRoles.map((r) => ({
                  value: r,
                  label: r.toLowerCase(),
                  description: ROLE_DESCRIPTION[r],
                }))}
                placeholder="Choose a role"
                searchPlaceholder="Search roles"
              />
            )}
          </div>

          {/* The bottom of the rate chain. An entry's own rate wins, then the
              activity, project and customer rates; these apply when none of those
              does. Internal is the COST side - what the person costs the company -
              and is what a margin figure is measured against. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="hourlyRate">Hourly rate (optional)</Label>
              <Input
                id="hourlyRate"
                inputMode="decimal"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                placeholder="1500"
              />
              <p className="text-xs text-muted-foreground">
                What their time bills at when nothing more specific is set. Empty
                clears it.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="internalRate">Internal rate (optional)</Label>
              <Input
                id="internalRate"
                inputMode="decimal"
                value={internalRate}
                onChange={(e) => setInternalRate(e.target.value)}
                placeholder="800"
              />
              <p className="text-xs text-muted-foreground">
                What their time costs. Never billed &mdash; it is the cost side of a
                margin.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Reports to (optional)</Label>
            <Combobox
              value={supervisorId}
              onChange={setSupervisorId}
              options={people
                .filter((p) => p.id !== person?.id)
                .map((p) => ({
                  value: p.id,
                  label: p.displayName,
                  description: p.jobTitle ?? undefined,
                }))}
              placeholder="Nobody in particular"
              searchPlaceholder="Search people"
            />
            <p className="text-xs text-muted-foreground">
              Who approves their weeks. Without one, their team leads do.
            </p>
          </div>

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Add person'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
